import { MAX_UPLOAD_RETRIES } from './shared-survey-mobile-ut-phone.consts';
import type {
  MobileUtSessionMutations,
  MobileUtUploadQueueItem,
} from './shared-survey-mobile-ut-phone.types';

interface ChunkUploaderOptions {
  uniqueId: string;
  createUploadUrl: MobileUtSessionMutations['createUploadUrl'];
  getMimeType: () => string;
}

export function createMobileUtChunkUploader({
  uniqueId,
  createUploadUrl,
  getMimeType,
}: ChunkUploaderOptions) {
  const queue: MobileUtUploadQueueItem[] = [];
  let isUploading = false;

  async function uploadChunkWithRetry(blob: Blob, index: number) {
    for (let attempt = 0; attempt <= MAX_UPLOAD_RETRIES; attempt += 1) {
      let failure: { status: number } | { error: unknown };
      try {
        const { data } = await createUploadUrl({
          variables: {
            input: {
              uniqueId,
              chunkIndex: index,
              mimeType: getMimeType(),
            },
          },
        });
        const url = data?.createMobileAudioUploadUrl?.url;
        if (!url) return;

        const response = await fetch(url, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': getMimeType() },
        });
        if (response.ok) return;
        failure = { status: response.status };
      } catch (error) {
        failure = { error };
      }

      if (attempt === MAX_UPLOAD_RETRIES) {
        console.error('[mobile-ut] audio chunk upload failed', {
          index,
          ...failure,
        });
        return;
      }
      await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
    }
  }

  async function processUploadQueue(): Promise<void> {
    if (isUploading) return;
    isUploading = true;

    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) break;
      await uploadChunkWithRetry(item.blob, item.index);
    }

    isUploading = false;
  }

  function enqueueChunkUpload(blob: Blob, index: number) {
    queue.push({ blob, index });
    void processUploadQueue();
  }

  return { enqueueChunkUpload };
}
